import {
	PensionPlanTransfer,
	TransferKind,
	TransferStatus
} from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { isLivePlan, isPersonalViewPlan, ViewOptionPlan } from './viewOptions';

/** `origin` con el que nacen las peticiones pedidas desde dentro de la plataforma. */
const PLATFORM_ORIGIN = 'PLATFORM';

/** Lo que pide un lead del funnel: abrir un plan o traer dinero de fuera. */
const FUNNEL_LEAD_KINDS: readonly TransferKind[] = ['EXTERNAL_TRANSFER', 'NEW_PLAN'];

/**
 * Estados en los que la petición sigue viva. `in_progress` cuenta: tras bloquear
 * el plan y firmar, el alta sigue en proceso hasta que operaciones la ejecuta.
 */
const LIVE_STATUSES: readonly TransferStatus[] = ['draft', 'requested', 'in_progress'];

/**
 * ¿Es un alta individual en proceso nacida en el funnel?
 *
 * 🔴 Mismo criterio que `isKycEligible` en comparsa (`kyc.service.ts`), que es
 * quien deja hacer el KYC a un empleado B2B: petición del funnel (con partner en
 * `origin`, nunca `PLATFORM` ni vacío), de alta o de traspaso, y viva. Si aquí se
 * dijera otra cosa, la plataforma le ofrecería una vista que el KYC le niega, o
 * al revés.
 *
 * Una petición sin `kind` es anterior al split y no se sabe qué pide: no cuenta.
 */
export function isLiveFunnelEnrollment(transfer: PensionPlanTransfer): boolean {
	if (!transfer.origin || transfer.origin === PLATFORM_ORIGIN) return false;
	if (!transfer.kind || !FUNNEL_LEAD_KINDS.includes(transfer.kind)) return false;
	return LIVE_STATUSES.includes(transfer.status as TransferStatus);
}

/** Las peticiones vivas del funnel, en el orden en que llegan. */
export const liveFunnelEnrollments = (transfers: PensionPlanTransfer[]): PensionPlanTransfer[] =>
	transfers.filter(isLiveFunnelEnrollment);

export const hasLiveFunnelEnrollment = (transfers: PensionPlanTransfer[]): boolean =>
	transfers.some(isLiveFunnelEnrollment);

/**
 * La petición que representa el alta en proceso: el traspaso si lo hay, y si no,
 * el alta.
 *
 * 🔴 Se elige por `kind` y no por la posición en la lista. El traspaso es el que
 * trae el plan de origen (su DGSFP y su saldo), así que es el que permite comparar;
 * el alta no tiene con qué. Hoy comparsa las devuelve con el traspaso delante, pero
 * eso es un efecto de `createdAt DESC`, y una petición de la plataforma creada
 * después bastaría para descolocarlo.
 */
export const resolveFunnelPrimaryTransfer = (
	transfers: PensionPlanTransfer[]
): PensionPlanTransfer | undefined => {
	const live = liveFunnelEnrollments(transfers);
	return live.find((transfer) => transfer.kind === 'EXTERNAL_TRANSFER') ?? live[0];
};

/**
 * ¿Tiene un plan individual EN PROCESO? Alta viva del funnel y todavía ningún
 * plan personal vivo: hasta que operaciones ejecuta el alta no existe su fila en
 * `user_epp_config`, y en cuanto existe, el plan real ocupa su sitio.
 *
 * Sin la lista de planes no se promete nada: podría tener ya su plan.
 */
export const hasPendingPersonalPlan = (
	transfers: PensionPlanTransfer[],
	plans: ViewOptionPlan[] | undefined
): boolean => {
	if (!plans) return false;
	if (plans.some((plan) => isLivePlan(plan) && isPersonalViewPlan(plan))) return false;
	return hasLiveFunnelEnrollment(transfers);
};
